import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  getBackupPreview,
  exportBackup,
  importBackup,
  wipeInstance,
  BackupImportMode,
  BackupImportResponse,
  BackupPreview,
  BackupWipeResponse,
} from '../api/backup';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableRow } from '../components/ui/table';
import { PageHeader } from '../components/PageHeader';

const MIN_PASSPHRASE_LENGTH = 16;
const CONFIRM_WORD = 'RESTORE';
const WIPE_WORD = 'DELETE';

const ROWS: { key: keyof BackupPreview; label: string }[] = [
  { key: 'flows', label: 'Flows' },
  { key: 'flowRevisions', label: 'Flow revisions' },
  { key: 'tags', label: 'Tags' },
  { key: 'envVars', label: 'Environment variables' },
  { key: 'oauthApps', label: 'OAuth apps' },
  { key: 'tokenStores', label: 'Tokens' },
  { key: 'users', label: 'Users' },
  { key: 'adminUsers', label: 'Admin users' },
  { key: 'apiKeys', label: 'API keys' },
  { key: 'syncStates', label: 'Sync cursors' },
];

function errorMessage(err: unknown): string {
  const response = (err as { response?: { data?: unknown } })?.response?.data;
  if (typeof response === 'string' && response) return response;
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong.';
}

export function AdminBackupPage() {
  const qc = useQueryClient();
  const { data: preview, isLoading, isError } = useQuery({
    queryKey: ['backup-preview'],
    queryFn: getBackupPreview,
  });

  const [passphrase, setPassphrase] = useState('');
  const [repeat, setRepeat] = useState('');
  const [includeExecutions, setIncludeExecutions] = useState(false);
  const [executionDays, setExecutionDays] = useState('7');

  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restorePassphrase, setRestorePassphrase] = useState('');
  const [mode, setMode] = useState<BackupImportMode>('Restore');
  const [confirmText, setConfirmText] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState<BackupImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wipeResult, setWipeResult] = useState<BackupWipeResponse | null>(null);

  const exportMutation = useMutation({
    mutationFn: exportBackup,
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setPassphrase('');
      setRepeat('');
      toast.success('Backup downloaded.');
    },
    onError: err => toast.error(`Export failed: ${errorMessage(err)}`),
  });

  const importMutation = useMutation({
    mutationFn: () => importBackup(restoreFile!, restorePassphrase, mode, overwrite),
    onSuccess: response => {
      setResult(response);
      setRestoreFile(null);
      setRestorePassphrase('');
      setConfirmText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['backup-preview'] });
      toast.success('Backup imported.');
    },
    onError: err => toast.error(`Import failed: ${errorMessage(err)}`),
  });

  const wipeMutation = useMutation({
    mutationFn: wipeInstance,
    onSuccess: response => {
      setWipeResult(response);
      setWipeConfirm('');
      qc.invalidateQueries({ queryKey: ['backup-preview'] });
      toast.success(`Deleted ${response.totalRowsDeleted} rows.`);
    },
    onError: err => toast.error(`Wipe failed: ${errorMessage(err)}`),
  });

  const passphrasesMatch = passphrase.length >= MIN_PASSPHRASE_LENGTH && passphrase === repeat;
  const canImport =
    restoreFile !== null &&
    restorePassphrase.length >= MIN_PASSPHRASE_LENGTH &&
    confirmText === CONFIRM_WORD;

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (isError) return <p className="text-destructive">Could not load the backup summary.</p>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Backup"
        description="Export the full configuration of this instance, or rebuild an empty instance from an earlier export."
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Export</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Table>
              <TableBody>
                {ROWS.map(row => (
                  <TableRow key={row.key}>
                    <TableCell className="py-1.5 text-muted-foreground">{row.label}</TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums">{preview?.[row.key] ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <p>
                The exported file contains every secret this instance holds — OAuth tokens, client
                secrets and environment variables — in plaintext, protected only by the passphrase
                you choose here.
              </p>
              <p className="mt-1">
                Store the passphrase separately from the file. Without it the backup cannot be
                restored.
              </p>
            </div>
          </div>

          <div className="grid max-w-md gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="passphrase">Passphrase</Label>
              <Input
                id="passphrase"
                type="password"
                value={passphrase}
                onChange={e => setPassphrase(e.target.value)}
                placeholder={`At least ${MIN_PASSPHRASE_LENGTH} characters`}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="passphrase-repeat">Repeat passphrase</Label>
              <Input
                id="passphrase-repeat"
                type="password"
                value={repeat}
                onChange={e => setRepeat(e.target.value)}
              />
              {repeat.length > 0 && passphrase !== repeat && (
                <p className="text-xs text-destructive">Passphrases do not match.</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-primary"
                checked={includeExecutions}
                onChange={e => setIncludeExecutions(e.target.checked)}
              />
              Include failed executions (for reproducing errors in a test environment)
            </label>
            {includeExecutions && (
              <div className="ml-6 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Label htmlFor="execution-days">Days back</Label>
                  <Input
                    id="execution-days"
                    type="number"
                    min={1}
                    max={90}
                    value={executionDays}
                    onChange={e => setExecutionDays(e.target.value)}
                    className="w-24"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Step inputs and outputs are stored unencrypted and routinely contain payloads and
                  personal data. Treat the export accordingly.
                </p>
              </div>
            )}
          </div>

          <Button
            disabled={!passphrasesMatch || exportMutation.isPending}
            onClick={() =>
              exportMutation.mutate({
                passphrase,
                includeExecutions,
                executionDays: Number(executionDays) || 7,
                failedOnly: true,
              })
            }
          >
            <Download className="h-4 w-4" />
            {exportMutation.isPending ? 'Preparing…' : 'Download backup'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Restore</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {overwrite
              ? 'Existing data will be deleted and replaced by the backup. Flows are restored dormant — their published revision is preserved, but you activate them yourself.'
              : 'A backup can only be imported into an empty instance. Flows are restored dormant — their published revision is preserved, but you activate them yourself.'}
          </p>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 accent-primary"
              checked={overwrite}
              onChange={e => setOverwrite(e.target.checked)}
            />
            <span>
              Overwrite existing data
              <span className="block text-xs text-muted-foreground">
                The backup is decrypted and checked first, then the deletion and the import happen
                together. A wrong passphrase leaves this instance untouched.
              </span>
            </span>
          </label>

          <div className="grid max-w-md gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="backup-file">Backup file</Label>
              <Input
                id="backup-file"
                ref={fileInputRef}
                type="file"
                accept=".ffbak"
                onChange={e => setRestoreFile(e.target.files?.[0] ?? null)}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="restore-passphrase">Backup passphrase</Label>
              <Input
                id="restore-passphrase"
                type="password"
                value={restorePassphrase}
                onChange={e => setRestorePassphrase(e.target.value)}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="import-mode">Mode</Label>
              <Select value={mode} onValueChange={value => setMode(value as BackupImportMode)}>
                <SelectTrigger id="import-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Restore">Full restore</SelectItem>
                  <SelectItem value="TestEnvironment">Test environment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {mode === 'TestEnvironment' ? (
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p>
                  Refresh tokens and API keys are discarded. Without a refresh token this instance
                  cannot rotate an OAuth credential, so it cannot lock the live instance out.
                </p>
                <p className="mt-1">
                  The copied access tokens are still live and talk to real APIs — a flow with
                  writing steps will write to production systems.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p>
                After a full restore the sign-in credentials from the backup apply — the admin
                account you just bootstrapped is replaced by the one in the file.
              </p>
            </div>
          )}

          <div className="grid max-w-md gap-1.5">
            <Label htmlFor="confirm">
              Type <span className="font-mono">{CONFIRM_WORD}</span> to confirm
            </Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              autoComplete="off"
            />
          </div>

          <Button
            variant="destructive"
            disabled={!canImport || importMutation.isPending}
            onClick={() => importMutation.mutate()}
          >
            <Upload className="h-4 w-4" />
            {importMutation.isPending ? 'Importing…' : 'Import backup'}
          </Button>

          {result && (
            <div className="space-y-1 rounded-md border border-border bg-card p-3 text-sm">
              <p className="font-medium">
                Imported {result.imported.flows} flows, {result.imported.tokenStores} tokens,{' '}
                {result.imported.users} users in {result.mode} mode
                {result.overwrote ? ', replacing the previous contents' : ''}.
              </p>
              <p className="text-muted-foreground">
                All flows are deactivated. Review them, then activate the ones that should run.
              </p>
              {!result.schemaVersionMatches && (
                <p className="text-warning">
                  The backup was taken on schema {result.backupSchemaVersion}, this instance runs{' '}
                  {result.currentSchemaVersion}. The import succeeded — verify your flows.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-destructive">Danger zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <p>
                Deletes every flow, revision, token, app, environment variable, tag, sync cursor,
                API key, user and execution. Only your own admin account survives, so you stay
                signed in.
              </p>
              <p className="mt-1">
                This cannot be undone. Export a backup first — and if you are about to restore one,
                use “Overwrite existing data” above instead, which cannot leave the instance empty.
              </p>
            </div>
          </div>

          <div className="grid max-w-md gap-1.5">
            <Label htmlFor="wipe-confirm">
              Type <span className="font-mono">{WIPE_WORD}</span> to confirm
            </Label>
            <Input
              id="wipe-confirm"
              value={wipeConfirm}
              onChange={e => setWipeConfirm(e.target.value)}
              autoComplete="off"
            />
          </div>

          <Button
            variant="destructive"
            disabled={wipeConfirm !== WIPE_WORD || wipeMutation.isPending}
            onClick={() => wipeMutation.mutate()}
          >
            <Trash2 className="h-4 w-4" />
            {wipeMutation.isPending ? 'Deleting…' : 'Delete all data'}
          </Button>

          {wipeResult && (
            <p className="text-sm text-muted-foreground">
              Deleted {wipeResult.totalRowsDeleted} rows. The instance is now empty apart from your
              admin account.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
